import React from 'react';
import { Box, Typography, Grid, Stack } from '@mui/material';
import { Client } from '../../types/client';
import { format } from 'date-fns';

interface CompanyDetailsSectionProps {
  client: Client;
  sectionStyle: any;
}

const CompanyDetailsSection: React.FC<CompanyDetailsSectionProps> = ({ client, sectionStyle }) => {
  const labelStyle = {
    color: '#666',
    fontWeight: 500,
    textTransform: 'uppercase' as const,
    fontSize: '0.75rem',
    letterSpacing: '0.5px',
    mb: 0.5
  };

  const valueStyle = {
    fontWeight: 500,
    color: '#1a1a1a'
  };

  return (
    <Box mb={4} sx={sectionStyle}>
      <Typography 
        variant="h6" 
        gutterBottom 
        sx={{ 
          fontWeight: 600,
          fontSize: '1.25rem',
          mb: 3,
          color: '#1976d2'
        }}
      >
        Company Details
      </Typography>
      <Grid container spacing={3}>
        <Grid item xs={12} md={6}>
          <Stack spacing={2}>
            <Box>
              <Typography variant="caption" sx={labelStyle}>
                Company Name
              </Typography>
              <Typography variant="body1" sx={valueStyle}>
                {client.companyName}
              </Typography>
            </Box>
            <Box>
              <Typography variant="caption" sx={labelStyle}>
                Client Liaison
              </Typography>
              <Typography variant="body1" sx={valueStyle}>
                {client.ownerName}
              </Typography>
            </Box>
            {client.position && (
              <Box>
                <Typography variant="caption" sx={labelStyle}>
                  Position
                </Typography>
                <Typography variant="body1" sx={valueStyle}>
                  {client.position}
                </Typography>
              </Box>
            )}
            <Box>
              <Typography variant="caption" sx={labelStyle}>
                Business Type
              </Typography>
              <Typography variant="body1" sx={valueStyle}>
                {client.businessType}
              </Typography>
            </Box>
            <Box>
              <Typography variant="caption" sx={labelStyle}>
                Email
              </Typography>
              <Typography variant="body1" sx={valueStyle}>
                {client.email}
              </Typography>
            </Box>
          </Stack>
        </Grid>
        <Grid item xs={12} md={6}>
          <Stack spacing={2}>
            {/* Show Trading Address for Sole Traders */}
            {client.businessType === 'Sole Trader' && (
              <Box>
                <Typography variant="caption" sx={labelStyle}>
                  Trading Address
                </Typography>
                <Typography variant="body1" sx={valueStyle}>
                  {client.tradingAddress?.line1}
                  {client.tradingAddress?.line2 && <>, {client.tradingAddress.line2}</>}
                  <br />
                  {client.tradingAddress?.city}
                  {client.tradingAddress?.county && <>, {client.tradingAddress.county}</>}
                  <br />
                  {client.tradingAddress?.postcode}
                </Typography>
              </Box>
            )}
            
            {/* Show Registered Address for Ltd Company/Charity */}
            {(['Limited Company', 'Charity / Trust / Community Org'].includes(client.businessType)) && (
              <>
                <Box>
                  <Typography variant="caption" sx={labelStyle}>
                    Registered Address
                  </Typography>
                  <Typography variant="body1" sx={valueStyle}>
                    {client.registeredBusinessAddress.line1}
                    {client.registeredBusinessAddress.line2 && <>, {client.registeredBusinessAddress.line2}</>}
                    <br />
                    {client.registeredBusinessAddress.city}
                    {client.registeredBusinessAddress.county && <>, {client.registeredBusinessAddress.county}</>}
                    <br />
                    {client.registeredBusinessAddress.postcode}
                  </Typography>
                </Box>
                <Box>
                  <Typography variant="caption" sx={labelStyle}>
                    Incorporated Date
                  </Typography>
                  <Typography variant="body1" sx={valueStyle}>
                    {client.incorporationDate && format(new Date(client.incorporationDate), 'dd-MM-yyyy')}
                  </Typography>
                </Box>
              </>
            )}
            
            {/* Show Trading Duration for Sole Traders if already entered */}
            {client.businessType === 'Sole Trader' && client.yearsTrading !== undefined && (
              <Box>
                <Typography variant="caption" sx={labelStyle}>
                  Trading Duration
                </Typography>
                <Typography variant="body1" sx={valueStyle}>
                  {client.yearsTrading} years {client.monthsTrading || 0} months
                </Typography>
              </Box>
            )}
          </Stack>
        </Grid>
      </Grid>
    </Box>
  );
};

export default CompanyDetailsSection;
